import apiSlice from '..';
import { Vehicle } from '../../../types/vehicle';
import { convertToFormData } from '../../../utils/api';
import { FieldLocalImage } from '../../uploadVehicle/assignVehicleImagesSlice';
import { SectionImage, SectionNotes } from '../vehicle-sections/types';
import { BlueBookInfo } from './types';

type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

type StartUploadImageData = {
  fileKey: string;
  signedUrl: string;
  objectUrl: string;
};

type StartUploadResponse = {
  mainImage: StartUploadImageData;
  previewImage: StartUploadImageData;
};

type StartUploadPayload = {
  vehicleId: number;
  imageId?: number;
  order?: number;
  sectionId?: number;
};

type EstimateCarPriceConfig = {
  trim: string;
  options: number[];
  engine: string;
  transmission: string;
  drive_train: string;
};

export type ImageField = {
  id: number;
  label: string;
  notes: string;
  url: string;
  url_thumb: string;
  type: string;
  is_damaged?: boolean;
  file?: File;
  local_url?: string;
  status?: string;
  expiration_date?: string;
};

export type UpdateSectionPayload = {
  sectionName: string;
  subSectionName?: string;
  notes?: string;
  grade?: string | null;
};

export type SaveImagePayload = {
  images: FieldLocalImage[];
  sectionName: string;
  vehicleId: number;
  subSectionName: string;
};

export type UpdateImagePayload = {
  vehicleId: number | string;
  id: number;
  order?: number;
  label?: string;
  notes?: string;
  type?: string;
  is_damaged?: boolean;
  file?: File;
};

export type GetVinInfoQueryResponse = {
  uploadType: 'manual' | 'automatic';
  blueBookData: BlueBookInfo[];
  hasDuplicate: boolean;
  statusMessage: string | null;
  duplicateTitle?: string;
};

type UploadVehiclePayload = { 
  vin: string; 
  seller_id: number; 
  upload_type?: string; 
  auction_type: string;
  copy_values?: boolean;
  copy_images?: boolean;
}

export const uploadVehicleApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getVinInfoByVehicleId: builder.query<BlueBookInfo[], number>({
      query: vehicleId => ({
        url: `vehicles/upload/vin-info/${vehicleId}`,
        method: 'GET'
      }),
      transformResponse: (r: any) => r.data
    }),
    getVehicleById: builder.query<Vehicle, number>({
      query: id => ({
        url: `vehicles/${id}`,
        method: 'GET'
      }),
      transformResponse: (r: any) => r.data,
      keepUnusedDataFor: 0,
      providesTags: ['VehicleById']
    }),
    uploadVehicle: builder.mutation<
      Vehicle,
      UploadVehiclePayload
    >({
      query: ({ vin, seller_id, upload_type, auction_type, copy_values, copy_images }) => ({
        url: `vehicles/upload?upload_type=${upload_type}`,
        method: 'POST',
        body: {
          vin,
          seller_id,
          auction_type,
          copy_images,
          copy_values
        }
      }),
      invalidatesTags: ['VehicleHistory', 'Sales', 'VinInfo'],
      transformResponse: (r: any) => r.data
    }),
    activateVehicle: builder.mutation<Vehicle, number>({
      query: vehicleId => ({
        url: `vehicles/${vehicleId}/activate`,
        method: 'POST'
      }),
      invalidatesTags: ['VehicleHistory', 'VehicleById'],
      transformResponse: (r: any) => r.data
    }),
    updateVehicleField: builder.mutation<{ data: Vehicle }, { payload: DeepPartial<Vehicle>; id: number }>({
      query: ({ payload, id }) => ({
        url: `vehicles/upload/${id}`,
        body: payload,
        method: 'PATCH'
      }),
      invalidatesTags: ['VehicleHistory']
    }),
    updateVehicle: builder.mutation<{ data: any }, { vehicleId: number; updateData: any }>({
      query: ({ vehicleId, updateData }) => ({
        url: `vehicles/${vehicleId}/update`,
        method: 'PUT',
        body: updateData
      }),
      invalidatesTags: ['VehicleById', 'VehicleSections', 'VehicleHistory']
    }),
    sellerUpdateVehicle: builder.mutation<void, { vehicleId: number; updateData: any }>({
      query: ({ vehicleId, updateData }) => ({
        url: `vehicles/${vehicleId}/seller-update`,
        method: 'PATCH',
        body: updateData
      }),
      invalidatesTags: ['VehicleHistory']
    }),
    uploadWithVinImage: builder.mutation<string, Blob>({
      query: image => {
        const formData = new FormData();
        formData.append('vin', image);
        return {
          url: `vehicles/upload/vin-image`,
          method: 'POST',
          body: formData,
          formData: true
        };
      },
      transformResponse: (r: any) => r.data
    }),
    estimateCarPrice: builder.mutation<
      { data: Partial<Vehicle> },
      { vehicleId: number; mileage: number; config?: EstimateCarPriceConfig }
    >({
      query: ({ vehicleId, mileage, config }) => ({
        url: `vehicles/upload/${vehicleId}/estimate-price`,
        body: { mileage, config },
        method: 'POST'
      })
    }),
    getVinInfo: builder.query<GetVinInfoQueryResponse, string>({
      query: vin => ({
        url: `vin_info/${vin}`,
        method: 'GET'
      }),
      transformResponse: (r: any) => r.data,
      providesTags: ['VinInfo']
    }),
    saveSectionImages: builder.mutation<
      { data: FieldLocalImage[]; sectionName: string; subSectionName: string },
      SaveImagePayload
    >({
      query: ({ vehicleId, sectionName, subSectionName, images }) => {
        const formData = new FormData();
        for (const i in images) {
          const image = images[i];
          if (image.file) {
            formData.append(`images[${i}][file]`, image.file);
          }
          if (image.id) {
            formData.append(`images[${i}][id]`, image.id?.toString());
          }
          if (image.url) {
            formData.append(`images[${i}][url]`, image.url);
          }
          if (image.url_thumb) {
            formData.append(`images[${i}][url_thumb]`, image.url_thumb);
          }
          if (image.label) {
            formData.append(`images[${i}][label]`, image.label);
          }
          if (image.notes) {
            formData.append(`images[${i}][notes]`, image.notes);
          }
          if (image.type) {
            formData.append(`images[${i}][type]`, image.type);
          }
          if (image.is_damaged != null) {
            formData.append(`images[${i}][is_damaged]`, image.is_damaged.toString());
          }
        }

        formData.append('sectionName', sectionName);
        formData.append('subSectionName', subSectionName);

        return {
          url: `vehicles/upload/images/${vehicleId}`,
          method: 'POST',
          body: formData,
          formData: true
        };
      }
    }),
    startImageUpload: builder.mutation<StartUploadResponse, StartUploadPayload>({
      query: ({ vehicleId, imageId, order, sectionId }) => {
        return {
          url: `vehicles/upload/images/${vehicleId}/start`,
          method: 'POST',
          body: {
            imageId,
            order,
            sectionId
          }
        };
      },
      transformResponse: (r: any) => r.data
    }),
    startVideoUpload: builder.mutation<StartUploadImageData, { vehicleId: number; ext: string; mimeType: string }>({
      query: ({ vehicleId, ext, mimeType }) => {
        return {
          url: `vehicles/upload/video/${vehicleId}/start`,
          method: 'POST',
          params: { ext, mimeType }
        };
      },
      transformResponse: (r: any) => r.data
    }),
    updateSectionImage: builder.mutation<{ data: ImageField }, UpdateImagePayload>({
      query: ({ vehicleId, id, ...payload }) => {
        const formData = convertToFormData(payload);

        return {
          url: `vehicles/upload/${vehicleId}/images/${id}`,
          method: 'POST',
          body: formData,
          formData: true
        };
      }
    }),
    deleteSectionImage: builder.mutation<void, number>({
      query: id => ({
        url: `vehicles/upload/images/${id}`,
        method: 'DELETE'
      })
    }),
    updateVehicleSection: builder.mutation<void, { vehicleId: number; payload: UpdateSectionPayload }>({
      query: ({ vehicleId, payload }) => ({
        url: `vehicles/upload/sections/${vehicleId}`,
        method: 'PATCH',
        body: payload
      })
    }),
    changeVin: builder.mutation<{ data: Vehicle }, { vehicleId: number; vin: string }>({
      query: ({ vehicleId, vin }) => ({
        url: `vehicles/change-vin/${vehicleId}`,
        method: 'POST',
        body: { vin }
      })
    }),
    submitVehicle: builder.mutation<void, { vehicleId: number; auctionType: string }>({
      query: ({ vehicleId, auctionType }) => ({
        url: `vehicles/upload/submit/${vehicleId}`,
        method: 'POST',
        body: { auctionType }
      })
    }),
    submitVehicleForReview: builder.mutation<void, { vehicleId: number }>({
      query: ({ vehicleId }) => ({
        url: `vehicles/upload/submit-for-review/${vehicleId}`,
        method: 'POST'
      })
    }),
    loadSectionAudio: builder.mutation<
      { data: { url: string } },
      { vehicleId: number; sectionId: number; audio: Blob | null }
    >({
      query: ({ vehicleId, audio, sectionId }) => {
        const formData = new FormData();
        if (audio) {
          formData.append('audio', audio);
        }

        return {
          url: `vehicles/${vehicleId}/sections/${sectionId}/audio`,
          method: 'POST',
          body: formData,
          formData: true
        };
      }
    }),
    removeSectionAudio: builder.mutation<{ data: { url: string } }, { vehicleId: number; sectionId: number }>({
      query: ({ vehicleId, sectionId }) => {
        return {
          url: `vehicles/${vehicleId}/sections/${sectionId}/audio`,
          method: 'DELETE'
        };
      }
    }),
    loadSectionVideo: builder.mutation<
      { data: { url: string } },
      { vehicleId: number; sectionId: number; video: Blob[] }
    >({
      query: ({ vehicleId, video, sectionId }) => {
        const formData = new FormData();
        if (video) {
          formData.append('video', new Blob(video, { type: 'video/mp4' }));
        }

        return {
          url: `vehicles/${vehicleId}/sections/${sectionId}/video`,
          method: 'POST',
          body: formData,
          formData: true
        };
      }
    }),
    getVideoUploadStatus: builder.query<
      { data: { status: string } },
      { vehicleId: number; sectionId: number; video: Blob[] }
    >({
      query: ({ vehicleId, video, sectionId }) => {
        return {
          url: `vehicles/${vehicleId}/sections/${sectionId}/video/status`,
          method: 'GET'
        };
      },
      transformResponse: (r: any) => r.data
    }),
    removeSectionVideo: builder.mutation<void, { vehicleId: number; sectionId: number }>({
      query: ({ vehicleId, sectionId }) => {
        return {
          url: `vehicles/${vehicleId}/sections/${sectionId}/video`,
          method: 'DELETE'
        };
      }
    }),
    addVehicleOption: builder.mutation<
      { data: { id: number } },
      { vehicleId: number; payload: { optionName: string; isTypical: boolean } }
    >({
      query: ({ vehicleId, payload }) => ({
        url: `/vehicles/upload/${vehicleId}/manual/options`,
        body: payload,
        method: 'POST'
      })
    }),
    changeVehicleOption: builder.mutation<
      { data: { id: number } },
      { vehicleId: number; optionId: number; payload: { optionName?: string; isTypical?: boolean } }
    >({
      query: ({ vehicleId, optionId, payload }) => ({
        url: `/vehicles/upload/${vehicleId}/manual/options/${optionId}`,
        body: payload,
        method: 'PATCH'
      })
    }),
    removeVehicleOption: builder.mutation<void, { vehicleId: number; optionId: number }>({
      query: ({ vehicleId, optionId }) => ({
        url: `/vehicles/upload/${vehicleId}/manual/${optionId}`,
        method: 'DELETE'
      })
    }),
    getAutoCheckReport: builder.mutation<string, string>({
      query: vin => ({
        url: `autocheck/${vin}/get-report`,
        method: 'POST'
      }),
      transformResponse: (r: any) => r.data
    }),
    editSectionImages: builder.mutation<
      void,
      {
        vehicleId: number;
        add: Array<SectionImage & Partial<SectionNotes>>;
        update: any;
        remove: number[];
        sectionUpdates?: Array<{
          entityId: number;
          entityType: 'section';
          payload: { video?: string | null };
        }>;
      }
    >({
      query: ({ vehicleId, ...body }) => {
        const formData = new FormData();
        for (const i in body.add) {
          const image = body.add[i];
          if (image.file) {
            formData.append(`add[${i}][file]`, image.file);
          }
          if (image.id) {
            formData.append(`add[${i}][id]`, image.id?.toString());
          }
          if (image.url) {
            formData.append(`add[${i}][url]`, image.url);
          }
          if (image.o) {
            formData.append(`add[${i}][o]`, image.o.toString());
          }
          if (image.url_thumb) {
            formData.append(`add[${i}][url_thumb]`, image.url_thumb);
          }
          if (image.grade) {
            formData.append(`add[${i}][grade]`, image.grade.toString());
          }
          if ('message' in image) {
            formData.append(`add[${i}][message]`, image.message || '');
          }
          if ('original_message' in image) {
            formData.append(`add[${i}][original_message]`, image.original_message || '');
          }
          if ('ai_message' in image) {
            formData.append(`add[${i}][ai_message]`, image.ai_message || '');
          }
          if ('status' in image) {
            formData.append(`add[${i}][status]`, image.status || '');
          }
          if (image.type) {
            formData.append(`add[${i}][type]`, image.type);
          }
          if (image.is_damaged != null) {
            formData.append(`add[${i}][is_damaged]`, image.is_damaged.toString());
          }
          if (image.vehicle_section_id) {
            formData.append(`add[${i}][vehicle_section_id]`, image.vehicle_section_id.toString());
          }
        }

        for (const i in body.remove) {
          formData.append(`remove[${i}]`, body.remove[i].toString());
        }

        const updateBody: Array<[string, Partial<SectionImage>]> = Object.entries(body.update);
        console.log(body.update, 'body.update');
        for (const i in Object.entries(body.update)) {
          const [key, updateObj] = updateBody[i];
          for (const updateKey in updateObj) {
            console.log(updateKey, 'updateKey');
            formData.append(`update[id_${key}][${updateKey}]`, updateObj[updateKey] || '');
          }
          formData.append(`update[id_${key}][placeholder]`, '_');
        }

        if (body.sectionUpdates) {
          for (const i in body.sectionUpdates) {
            const u = body.sectionUpdates[i];
            formData.append(`sectionUpdates[${i}][entityId]`, u.entityId.toString());
            formData.append(`sectionUpdates[${i}][entityType]`, u.entityType);
            for (const payloadKey in u.payload) {
              formData.append(`sectionUpdates[${i}][payload][${payloadKey}]`, u.payload[payloadKey] ?? '');
            }
          }
        }

        return {
          url: `/vehicles/upload/${vehicleId}/images/edit`,
          method: 'PATCH',
          body: formData,
          formData: true
        };
      },
      transformResponse: (r: any) => r.data,
      invalidatesTags: ['VehicleSections']
    }),
    getSectionIds: builder.query<Record<string, number>, number>({
      query: vehicleId => ({
        url: `/vehicles/${vehicleId}/sections/ids`,
        method: 'GET'
      }),
      transformResponse: (r: any) => r.data
    })
  }),
  overrideExisting: true
});

export const useGetVinInfoQueryState = uploadVehicleApiSlice.endpoints.getVinInfo.useQueryState;

export const {
  useGetVehicleByIdQuery,
  useUploadVehicleMutation,
  useUpdateVehicleFieldMutation,
  useEstimateCarPriceMutation,
  useUpdateVehicleSectionMutation,
  useSaveSectionImagesMutation,
  useUpdateSectionImageMutation,
  useDeleteSectionImageMutation,
  useChangeVinMutation,
  useGetVinInfoByVehicleIdQuery,
  useSubmitVehicleMutation,
  useSubmitVehicleForReviewMutation,
  useLoadSectionAudioMutation,
  useAddVehicleOptionMutation,
  useRemoveVehicleOptionMutation,
  useChangeVehicleOptionMutation,
  useRemoveSectionAudioMutation,
  useGetVinInfoQuery,
  useLazyGetVinInfoQuery,
  useGetAutoCheckReportMutation,
  useActivateVehicleMutation,
  useUpdateVehicleMutation,
  useEditSectionImagesMutation,
  useGetSectionIdsQuery,
  useUploadWithVinImageMutation,
  useLoadSectionVideoMutation,
  useRemoveSectionVideoMutation,
  useStartImageUploadMutation,
  useStartVideoUploadMutation,
  useSellerUpdateVehicleMutation
} = uploadVehicleApiSlice;

export default uploadVehicleApiSlice;
